import React, { useRef, useState, useEffect } from 'react';
import cx from 'classnames';

import { SuccessBadge, WarningBadge } from '@magoosh/lib/badge';
import { DefaultButton } from '@magoosh/lib/button';
import Collapse from '@magoosh/lib/collapse';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { pluralize } from 'utilities/pluralize';

import Facet from '../facet';
import NoContent from '../no_content';
import { Answer, SpeakingScoreReportPart } from '../../../types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  answer: Answer;
  reportType: string;
  scoreReportPart: SpeakingScoreReportPart;
  volume: number;
  setVolume: (volume: number) => void;
}

export default (props: Props) => {
  const promptAudioRef = useRef<HTMLAudioElement | null>(null);
  const responseAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlayingPrompt, setIsPlayingPrompt] = useState(false);
  const [isPlayingResponse, setIsPlayingResponse] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (promptAudioRef.current) {
        promptAudioRef.current.pause();
        promptAudioRef.current = null;
      }
      if (responseAudioRef.current) {
        responseAudioRef.current.pause();
        responseAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Update volume for both audio elements when volume changes
    if (promptAudioRef.current) {
      promptAudioRef.current.volume = props.volume;
    }
    if (responseAudioRef.current) {
      responseAudioRef.current.volume = props.volume;
    }
  }, [props.volume]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.setVolume(parseFloat(e.target.value));
  };

  const toggleMute = () => {
    props.setVolume(props.volume === 0 ? 1 : 0);
  };

  const getVolumeIcon = () => {
    if (props.volume === 0) return 'volume-off';
    if (props.volume < 0.5) return 'volume-down';
    return 'volume-up';
  };

  const playPromptAudio = () => {
    if (!promptAudioRef.current) {
      promptAudioRef.current = new Audio(props.answer.prompt.content.audio.url);
      promptAudioRef.current.volume = props.volume;
      promptAudioRef.current.addEventListener('ended', () => setIsPlayingPrompt(false));
      promptAudioRef.current.addEventListener('pause', () => setIsPlayingPrompt(false));
    }

    if (isPlayingPrompt) {
      promptAudioRef.current.pause();
      promptAudioRef.current.currentTime = 0;
      setIsPlayingPrompt(false);
    } else {
      // Pause response audio if playing
      if (responseAudioRef.current && isPlayingResponse) {
        responseAudioRef.current.pause();
        responseAudioRef.current.currentTime = 0;
        setIsPlayingResponse(false);
      }

      promptAudioRef.current.play();
      setIsPlayingPrompt(true);
    }
  };

  const playResponseAudio = () => {
    if (!responseAudioRef.current) {
      responseAudioRef.current = new Audio(props.answer.response.audio.url);
      responseAudioRef.current.volume = props.volume;
      responseAudioRef.current.addEventListener('ended', () => setIsPlayingResponse(false));
      responseAudioRef.current.addEventListener('pause', () => setIsPlayingResponse(false));
    }

    if (isPlayingResponse) {
      responseAudioRef.current.pause();
      responseAudioRef.current.currentTime = 0;
      setIsPlayingResponse(false);
    } else {
      // Pause prompt audio if playing
      if (promptAudioRef.current && isPlayingPrompt) {
        promptAudioRef.current.pause();
        promptAudioRef.current.currentTime = 0;
        setIsPlayingPrompt(false);
      }

      responseAudioRef.current.play();
      setIsPlayingResponse(true);
    }
  };
  return (
    <div className="d-flex flex-column gap-m">
      <div className="d-flex flex-1 flex-column flex-md-row gap-m">
        <div className="align-items-center d-flex flex-1 gap-s" style={{ minWidth: 0 }}>
          <h4
            className="u-margin-A-n"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            <strong>
              {
                props.answer.prompt.content.audio ? (
                  props.answer.prompt.content.audio.transcript
                ) : 'Response'
              }
            </strong>
          </h4>

          {
            props.scoreReportPart?.scoreInfluence === 'promoter' && (
              <div><SuccessBadge>Strong</SuccessBadge></div>
            )
          }
          {
            props.scoreReportPart?.scoreInfluence === 'detractor' && (
              <div><WarningBadge>Detractor</WarningBadge></div>
            )
          }
        </div>

        <div className="d-flex flex-none gap-s justify-content-md-end align-items-center">
          {
            props.answer.prompt.content.audio && (
              <DefaultButton size="xs" onClick={playPromptAudio}>
                <FontAwesomeIcon icon={isPlayingPrompt ? "pause" : "play"} /> Prompt
              </DefaultButton>
            )
          }

          <DefaultButton size="xs" onClick={playResponseAudio}>
            <FontAwesomeIcon icon={isPlayingResponse ? "pause" : "play"} /> <span className="hidden-xs">Your </span>Response
          </DefaultButton>

          <div className="d-flex align-items-center gap-xs">
            <div
              className="d-flex justify-content-center"
              onClick={() => setShowVolumeSlider(!showVolumeSlider)}
              style={{ cursor: 'pointer', width: '16px' }}
            >
              <FontAwesomeIcon icon={getVolumeIcon()} />
            </div>

            {showVolumeSlider && (
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={props.volume}
                onChange={handleVolumeChange}
                style={{ width: '70px', cursor: 'pointer' }}
                title={`Volume: ${Math.round(props.volume * 100)}%`}
              />
            )}
          </div>
        </div>
      </div>

      {
        props.scoreReportPart ? (
          <>
            <div
              className={cx(
                style.partScoreAndNarrative,
                {
                  [style.detractor]: props.scoreReportPart.scoreInfluence === 'detractor',
                  [style.promoter]: props.scoreReportPart.scoreInfluence === 'promoter'
                }
              )}
            >
              <div className="small">
                <strong>Score {props.scoreReportPart.score}</strong>
              </div>

              <div className="small">
                {props.scoreReportPart.content.narrative}
              </div>
            </div>

            <div>
              <Collapse>
                <Collapse.Trigger>
                  {({ isExpanded }) => {
                    return (
                      <div className="align-items-center d-flex flex-1 gap-xs small u-margin-B-s">
                        <Collapse.Chevron isExpanded={isExpanded} />

                        <div className="text-bold">
                          {isExpanded ? 'Hide' : 'Show'} analysis
                        </div>
                      </div>
                    );
                  }}
                </Collapse.Trigger>

                <div className="d-flex flex-column gap-m">
                  <div className="d-flex flex-column gap-s">
                    <div>Vocal facets</div>

                    {
                      props.reportType === 'Interview' ? (
                        <>
                          <Facet
                            name="Relevance"
                            range={{
                              low: { label: 'Low', value: 0 },
                              high: { label: 'High', value: 100 }
                            }}
                            value={props.scoreReportPart.content.facets.relevance}
                          />

                          <Facet
                            name="Pronunciation"
                            range={{
                              low: { label: 'Needs Work', value: 0 },
                              high: { label: 'Perfect', value: 100 }
                            }}
                            value={props.scoreReportPart.content.facets.pronunciation}
                          />

                          <Facet
                            name="Grammar"
                            range={{
                              low: { label: 'Needs Work', value: 0 },
                              high: { label: 'Perfect', value: 100 }
                            }}
                            value={props.scoreReportPart.content.facets.grammar}
                          />

                          <Facet
                            name="Speed"
                            range={{
                              low: { label: 'Needs Work', value: 0 },
                              high: { label: 'On Target', value: 100 }
                            }}
                            tooltip="Aim for around 150 WPM"
                            value={
                              Math.max(
                                Math.min(
                                  100 - Math.abs(150 - props.scoreReportPart.content.facets.speed),
                                  100
                                ),
                                0
                              )
                            }
                            valueContext={`${props.scoreReportPart.content.facets.speed} WPM`}
                          />
                        </>
                      ) : (
                        <>
                          <Facet
                            name="Similarity"
                            range={{
                              low: { label: 'Different', value: 0 },
                              high: { label: 'Identical', value: 100 }
                            }}
                            value={props.scoreReportPart.content.facets.completeness}
                          />

                          <Facet
                            name="Pronunciation"
                            range={{
                              low: { label: 'Needs Work', value: 0 },
                              high: { label: 'Perfect', value: 100 }
                            }}
                            value={props.scoreReportPart.content.facets.pronunciation}
                          />

                          <Facet
                            name="Pauses"
                            range={{
                              low: { label: 'Many', value: 0 },
                              high: { label: 'None', value: 100 }
                            }}
                            value={
                              Math.max(
                                Math.min(
                                  100 - (props.scoreReportPart.content.facets.pauses * 25),
                                  100
                                ),
                                0
                              )
                            }
                            valueContext={pluralize(props.scoreReportPart.content.facets.pauses, 'Pause')}
                          />

                          <Facet
                            name="Speed"
                            range={{
                              low: { label: 'Needs Work', value: 0 },
                              high: { label: 'On Target', value: 100 }
                            }}
                            tooltip="Aim for around 150 WPM"
                            value={
                              Math.max(
                                Math.min(
                                  100 - Math.abs(150 - props.scoreReportPart.content.facets.speed),
                                  100
                                ),
                                0
                              )
                            }
                            valueContext={`${props.scoreReportPart.content.facets.speed} WPM`}
                          />
                        </>
                      )
                    }

                  </div>

                  <div className="d-flex flex-column gap-s u-padding-Hs">
                    <div>Transcript</div>

                    <div className="small">
                      <strong>Yours:</strong> {props.answer.response.audio.transcript}
                    </div>

                    {
                      props.answer.prompt.content.audio && (
                        <div className="small">
                          <strong>Prompt:</strong> {props.answer.prompt.content.audio.transcript}
                        </div>
                      )
                    }
                  </div>
                </div>
              </Collapse>
            </div>
          </>
        ) : (
          <NoContent />
        )
      }
    </div>
  );
};
